import * as React from 'react';

import {
  Button,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogOverlay,
  Select,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Move } from 'lucide-react';

import { $moveFile } from '../../_state/fileActions.state';
import {
  $selectedFileForAction,
  $showMoveDialog,
} from '../../_state/fileDialogs.state';
import { $files } from '../../_state/files.state';
import { $directoriesById } from '../../_state/fileTree.state';
import { useTranslations } from '../../i18n/useTranslations';

export const FileMoveDialog = () => {
  const t = useTranslations();
  const toast = useToast();
  const [isOpen, setIsOpen] = useAtom($showMoveDialog);
  const selectedFileId = useAtomValue($selectedFileForAction);
  const files = useAtomValue($files);
  const { data: directoriesById } = useAtomValue($directoriesById);
  const moveFile = useSetAtom($moveFile);

  const selectedFile = files.find((f) => f.id === selectedFileId);

  const [targetFolderId, setTargetFolderId] = React.useState<string>('root');
  const [isMoving, setIsMoving] = React.useState(false);

  // Get all folders excluding the current file (if it's a folder) and its children
  const availableFolders = React.useMemo(() => {
    if (!directoriesById) return [];

    const folders = Array.from(directoriesById.values());

    // If moving a folder, exclude itself and its descendants
    if (selectedFile?.type === 'Directory' && selectedFileId) {
      return folders.filter((folder) => {
        // Exclude the folder itself
        if (folder.id === selectedFileId) return false;

        // Exclude descendants by checking if this folder is in the path
        // path is string[], so we need to check if selectedFileId is in it
        return !folder.path.some((pathId) => pathId === selectedFileId);
      });
    }

    return folders;
  }, [directoriesById, selectedFile, selectedFileId]);

  const handleMove = async () => {
    if (!selectedFileId) {
      toast.error(t.dialogs.move.errorNoFile);
      return;
    }

    const newParentId = targetFolderId === 'root' ? null : targetFolderId;

    if (newParentId === selectedFile?.parentId) {
      // Already in this folder, just close
      setIsOpen(false);
      return;
    }

    setIsMoving(true);

    try {
      await moveFile({ fileId: selectedFileId, newParentId });
      const targetName =
        targetFolderId === 'root'
          ? 'root'
          : directoriesById?.get(targetFolderId)?.name || 'selected folder';
      toast.success(
        t.dialogs.move.successMoved
          .replace('{name}', selectedFile?.name || '')
          .replace('{destination}', targetName),
      );
      setIsOpen(false);
      setTargetFolderId('root');
    } catch (error) {
      toast.error(
        t.dialogs.move.errorFailed.replace(
          '{error}',
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    } finally {
      setIsMoving(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setTargetFolderId('root');
  };

  if (!selectedFile) {
    return null;
  }

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      className="@FileMoveDialog"
    >
      <DialogContent position="center">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold">
            <Move className="size-5" />
            {selectedFile.type === 'Directory'
              ? t.dialogs.move.titleFolder
              : t.dialogs.move.titleFile}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 p-4">
          <p className="text-sm text-neutral/70">
            {t.dialogs.move.moving.replace('{name}', selectedFile.name)}
          </p>
          <Select
            label={t.dialogs.move.labelDestination}
            value={targetFolderId}
            onChange={(key) => setTargetFolderId(key)}
            options={[
              { key: 'root', label: t.dialogs.move.optionRoot },
              ...availableFolders
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((folder) => ({
                  key: folder.id,
                  label:
                    folder.path.length > 0
                      ? `${folder.path.map((id) => directoriesById?.get(id)?.name || id).join(' / ')} / ${folder.name}`
                      : folder.name,
                })),
            ]}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onPress={handleClose} isDisabled={isMoving}>
            {t.dialogs.move.cancel}
          </Button>
          <Button
            variant="solid"
            onPress={handleMove}
            isDisabled={isMoving}
            isLoading={isMoving}
          >
            {t.dialogs.move.move}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
